import { useMemoizedFn } from 'ahooks';
import { uniqueId } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, Card, Space } from 'tdesign-react';
import { isValidJSON, sleep } from '@/utils/common';
import { createTempTopic } from '@/slices/messageSARSlice';
import BuriedWrapper from '@/components/BuriedWrapper';
import SvgIcon from '@/components/SvgIcon';
import Item from './Item';

const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const { host } = window.location;

const STATUS = {
  CONNECTING: '连接中',
  CONNECTED: '已连接',
  DISCONNECTED: '已断开',
  CLOSING: '正在断开',
};

function Receive({ currentTopic, submitting, createTempTopic }) {
  const socket = useRef();
  const bodyRef = useRef();
  const [messageNodes, setMessageNodes] = useState([]);
  const [connectState, setConnectState] = useState(STATUS.DISCONNECTED);

  const pushToMessages = (data) => {
    setMessageNodes((nodes) => {
      // 最多保留200个记录
      const last = [...nodes, { key: uniqueId(), ...data }].slice(-200);
      return last;
    });
  };

  // 断开连接
  const doClose = () => {
    if (socket.current) {
      socket.current.close();
      socket.current = null;
      setConnectState(STATUS.CLOSING);
    }
  };

  const doInit = useMemoizedFn(async () => {
    try {
      // 先创建Topic
      await createTempTopic(currentTopic);
      const currentSocket = new WebSocket(`${protocol}//${host}/consume`);

      // 连接命令
      const connectStr = JSON.stringify({
        cluster: currentTopic.clusterName,
        topic: currentTopic.fullTopicName,
      });

      currentSocket.onopen = () => {
        setConnectState(STATUS.CONNECTED);
        // pushToMessages({ txt: '连接成功，准备接收消息' });
        currentSocket.send(connectStr);
      };

      currentSocket.onmessage = async (event) => {
        let { data } = event;
        if (isValidJSON(data)) {
          data = JSON.parse(event.data);
          const { messageId, data: innerData } = data;
          data = {
            messageId,
            txt: innerData,
          };
        } else {
          data = {
            txt: data,
          };
        }
        pushToMessages(data);
        await sleep(100);
        // 滚动到最底部
        bodyRef.current?.scrollTo(0, bodyRef.current.scrollHeight);
      };
      currentSocket.onclose = () => {
        pushToMessages({ txt: '连接断开' });
        setConnectState(STATUS.DISCONNECTED);
      };

      currentSocket.onerror = (err) => {
        console.trace(err);
        doClose();
      };

      setConnectState(STATUS.CONNECTING);
      socket.current = currentSocket;
    } catch (error) {
      console.trace(error);
    }
  });

  useEffect(() => {
    doInit();
    document.addEventListener('unload', doClose);
    return () => {
      removeEventListener('unload', doClose);
      doClose();
    };
  }, [doInit, currentTopic]);

  const doClear = () => {
    setMessageNodes([]);
  };

  const loading = useMemo(() => {
    if (connectState === STATUS.CONNECTING) {
      return true;
    }
    if (connectState === STATUS.CLOSING) {
      return true;
    }
    return false;
  }, [connectState]);

  return (
    <Root shadow>
      <Title>
        <Icon src={require('../images/user.png')} /> 消费者
      </Title>

      <Body className='narrow-scrollbar' ref={bodyRef}>
        {messageNodes.map((item) => (
          <Item key={item.key} info={item} />
        ))}
      </Body>

      <Footer>
        <Space style={{ flex: 1, justifyContent: 'space-between' }}>
          <BuriedWrapper eventId='MESSAGE_SEND_RECEIVE_BREAKDOWN'>
            <Button
              loading={loading}
              theme='danger'
              onClick={doClose}
              variant='outline'
              icon={<SvgIcon type='ban' />}
              disabled={connectState === STATUS.DISCONNECTED}
            >
              断开
            </Button>
          </BuriedWrapper>
          <Space>
            <BuriedWrapper eventId='MESSAGE_SEND_RECEIVE_CONSUMER_CLEANUP'>
              <Button onClick={doClear} variant='outline'>
                清空
              </Button>
            </BuriedWrapper>
            <BuriedWrapper eventId='MESSAGE_SEND_RECEIVE_CONNECT'>
              <Button
                onClick={doInit}
                loading={submitting || loading}
                disabled={connectState === STATUS.CONNECTED}
              >
                连接
              </Button>
            </BuriedWrapper>
          </Space>
        </Space>
      </Footer>
    </Root>
  );
}

export default connect(
  (state) => ({
    currentTopic: state.messageSAR.currentTopic,
    submitting: state.messageSAR.submitting,
  }),
  {
    createTempTopic,
  },
)(Receive);

const Root = styled(Card)`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 16px;
  border: none;
  .t-card__body {
    display: flex;
    flex-direction: column;
    padding: 0;
    flex: 1;
  }
`;

const Footer = styled.div`
  height: 68px;
  display: flex;
  align-items: center;
  padding: 0 20px;
`;

const Body = styled.div`
  width: 100%;
  flex: 1;
  color: #bfff16;
  line-height: 18px;
  font-family: monospace;
  font-size: 12px;
  background: rgb(43, 43, 43);
  padding: 14px 20px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
  padding: 22px 20px;
  display: flex;
`;

const Icon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 8px;
`;
